/**
 * ReviewPage — Bulk-Review aller noch nicht freigegebenen Ankaufsprofile.
 *
 * Designed für effiziente menschliche Freigabe der ~1 800 Profile aus dem
 * AI-Recherche-Backlog. Kein API-Credit-Verbrauch, da rein manueller Workflow.
 *
 * Features:
 * - Filter (Investorenart, AI-Confidence, Quelle)
 * - Sortierung (höchste Confidence zuerst → schnelle Treffer)
 * - Bulk-Selection + Bulk-Approve
 * - Per-Zeile: Quick-Approve / Quick-Reject (= soft-delete)
 * - Pagination (200 pro Seite)
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';

interface ProfileRow {
  id: string;
  company_id: string;
  company_name: string;
  profile_name: string | null;
  source: string | null;
  ai_confidence: number | null;
  profile_approved: boolean;
  asset_class_labels: string[];
  volume_min_eur: number | null;
  volume_max_eur: number | null;
  target_countries: string[] | null;
  risk_profile: string | null;
  created_at: string;
}

interface InvestorType { id: string; code: string; label_de: string }

function fmtVolume(min: number | null, max: number | null): string {
  if (min == null && max == null) return '—';
  const f = (v: number | null) => v == null ? '?' : v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)} M€` : `${(v / 1_000).toFixed(0)} k€`;
  return `${f(min)}–${f(max)}`;
}

export function ReviewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [source, setSource] = useState<string>('');
  const [investorTypeId, setInvestorTypeId] = useState<string>('');
  const [minConfidence, setMinConfidence] = useState<string>('');
  const [sort, setSort] = useState<'confidence_desc' | 'confidence_asc' | 'company'>('confidence_desc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const { data: typesData } = useQuery<{ data: InvestorType[] }>({
    queryKey: ['investor-types'],
    queryFn: async () => (await apiClient.get('/companies/investor-types')).data,
    staleTime: 300_000,
  });

  const params = useMemo(() => {
    const p: Record<string, string> = {
      approved: 'false',
      is_active: 'true',
      page: String(page),
      limit: String(limit),
      sort,
    };
    if (source) p.source = source;
    if (investorTypeId) p.investor_type_id = investorTypeId;
    if (minConfidence) p.min_confidence = minConfidence;
    return p;
  }, [page, limit, source, investorTypeId, minConfidence, sort]);

  const { data, isLoading, refetch } = useQuery<{ data: ProfileRow[]; total: number }>({
    queryKey: ['review-profiles', params],
    queryFn: async () => (await apiClient.get('/profiles', { params })).data,
    staleTime: 0,
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  async function approveOne(id: string) {
    setBusy(true);
    try {
      await apiClient.patch(`/research/profiles/${id}/review`, { approved: true });
      await refetch();
      const next = new Set(selected); next.delete(id); setSelected(next);
    } finally { setBusy(false); }
  }

  async function rejectOne(id: string) {
    if (!confirm('Profil ablehnen und löschen?')) return;
    setBusy(true);
    try {
      await apiClient.delete(`/profiles/${id}`);
      await refetch();
      const next = new Set(selected); next.delete(id); setSelected(next);
    } finally { setBusy(false); }
  }

  async function bulkApprove() {
    if (selected.size === 0) return;
    if (!confirm(`${selected.size} Profile auf "freigegeben" setzen?`)) return;
    setBusy(true);
    try {
      await apiClient.post('/profiles/bulk-approve', { ids: [...selected] });
      setSelected(new Set());
      await refetch();
      await qc.invalidateQueries({ queryKey: ['dashboard-profile-coverage'] });
    } finally { setBusy(false); }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-header-title">Review-Queue</div>
          <div className="page-header-sub">Ungeprüfte Ankaufsprofile freigeben oder ablehnen ({total.toLocaleString('de-DE')} offen)</div>
        </div>
      </div>

      <div className="content">
        <div className="card" style={{ padding: '12px 16px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ fontSize: 12, color: 'var(--text3)' }}>Investorenart:</label>
          <select value={investorTypeId} onChange={e => { setInvestorTypeId(e.target.value); setPage(1); }}
            style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12 }}>
            <option value="">— alle —</option>
            {(typesData?.data ?? []).map(t => <option key={t.id} value={t.id}>{t.label_de}</option>)}
          </select>

          <label style={{ fontSize: 12, color: 'var(--text3)' }}>Quelle:</label>
          <select value={source} onChange={e => { setSource(e.target.value); setPage(1); }}
            style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12 }}>
            <option value="">— alle —</option>
            <option value="web_extraction">Web-Extraktion</option>
            <option value="pdf_import">PDF-Import</option>
            <option value="manual">Manuell</option>
            <option value="ai_extraction">AI-Extraktion</option>
          </select>

          <label style={{ fontSize: 12, color: 'var(--text3)' }}>Min. Confidence:</label>
          <select value={minConfidence} onChange={e => { setMinConfidence(e.target.value); setPage(1); }}
            style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12 }}>
            <option value="">— alle —</option>
            <option value="0.8">≥ 0.80</option>
            <option value="0.7">≥ 0.70</option>
            <option value="0.5">≥ 0.50</option>
          </select>

          <label style={{ fontSize: 12, color: 'var(--text3)' }}>Sortierung:</label>
          <select value={sort} onChange={e => setSort(e.target.value as typeof sort)}
            style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 12 }}>
            <option value="confidence_desc">Confidence ↓</option>
            <option value="confidence_asc">Confidence ↑</option>
            <option value="company">Firma A–Z</option>
          </select>

          <div style={{ flex: 1 }} />

          <button disabled={selected.size === 0 || busy} onClick={bulkApprove}
            style={{ padding: '8px 16px', background: selected.size > 0 ? '#2e7d32' : 'var(--surface2)', color: selected.size > 0 ? '#fff' : 'var(--text3)', border: 'none', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600, cursor: selected.size > 0 ? 'pointer' : 'not-allowed' }}>
            ✓ {selected.size} freigeben
          </button>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Lädt…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Keine ungeprüften Profile mit diesen Filtern.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', background: 'var(--surface2)' }}>
                  <th style={{ padding: '8px 6px', textAlign: 'center', width: 30 }}>
                    <input type="checkbox" checked={rows.length > 0 && selected.size === rows.length} onChange={toggleAll} />
                  </th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Firma</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Profil</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Asset-Klassen</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Volumen</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Länder</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Quelle</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Conf.</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px', textAlign: 'center' }}>
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} />
                    </td>
                    <td style={{ padding: '6px 8px', cursor: 'pointer', color: 'var(--accent)' }} onClick={() => navigate(`/companies/${r.company_id}`)}>
                      {r.company_name}
                    </td>
                    <td style={{ padding: '6px 8px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.profile_name ?? ''}>
                      {r.profile_name ?? '—'}
                    </td>
                    <td style={{ padding: '6px 8px', maxWidth: 280, fontSize: 11, color: 'var(--text2)' }}>
                      {(r.asset_class_labels ?? []).slice(0, 4).join(', ')}{(r.asset_class_labels?.length ?? 0) > 4 ? ` +${(r.asset_class_labels?.length ?? 0) - 4}` : ''}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: 11 }}>
                      {fmtVolume(r.volume_min_eur, r.volume_max_eur)}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 11 }}>
                      {(r.target_countries ?? []).slice(0, 4).join(', ')}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 11, color: 'var(--text3)' }}>{r.source ?? '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'DM Mono, monospace',
                      color: (r.ai_confidence ?? 0) >= 0.8 ? '#2e7d32' : (r.ai_confidence ?? 0) >= 0.5 ? '#f57c00' : '#e65100' }}>
                      {r.ai_confidence != null ? r.ai_confidence.toFixed(2) : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button disabled={busy} onClick={() => approveOne(r.id)}
                        style={{ padding: '4px 10px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', marginRight: 4 }}>
                        ✓
                      </button>
                      <button disabled={busy} onClick={() => rejectOne(r.id)}
                        style={{ padding: '4px 10px', background: '#fff', color: '#c62828', border: '1px solid #c62828', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {totalPages > 1 && (
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                Seite {page} / {totalPages} · {total.toLocaleString('de-DE')} Profile
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                  style={{ padding: '6px 12px', border: '1px solid var(--border)', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>← Zurück</button>
                <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  style={{ padding: '6px 12px', border: '1px solid var(--border)', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Weiter →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
